import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Ion } from "@/components/Ion";
import { Screen, Spinner, useBack } from "@/components/Orig";
import { useTheme } from "@/lib/theme";

const tone = (s, accent) => s >= 80 ? [accent, "ON TRACK"] : s >= 50 ? ["#F5A623", "GROWING"] : s > 0 ? ["#FF8A3D", "SLOW START"] : ["#E23E3E", "NO ACTIVITY"];
const card = "mx-4 rounded-[14px] border p-4";

const SalesTalkCoach = ({ theme }) => {
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [sel, setSel] = useState(null);
  const [industry, setIndustry] = useState("");
  const [loading, setLoading] = useState(false);
  const [resp, setResp] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { api("/health-check/coach/topics").then((r) => setTopics(r.topics)).catch(() => {}); }, []);
  const gen = async (t) => {
    setSel(t); setLoading(true); setErr(null); setResp(null);
    try { setResp(await api("/health-check/coach", { method: "POST", body: { topic: t.key, prospect_industry: industry.trim() || undefined } })); }
    catch (e) { setErr(e.message || "Coach couldn't reach the AI right now."); } finally { setLoading(false); }
  };
  const openSection = () => { const [pb, ph] = (resp?.playbook_section_slug || "").split("/"); if (pb) navigate(`/playbook/${pb}/${ph}`); };
  return (
    <div className={`${card} mt-2`} style={{ background: theme.card, borderColor: theme.border }} data-testid="sales-talk-coach">
      <div className="flex items-center">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ background: theme.accent }}><Ion name="chatbubbles" size={16} color="#000" /></span>
        <div className="ml-2 flex-1"><p className="text-[11px] font-black tracking-[1.5px]">ASK THE COACH</p><p className="mt-0.5 text-xs" style={{ color: theme.textSecondary }}>Tap a topic — get a natural, playbook-aligned script you can actually say out loud.</p></div>
      </div>
      <div className="mt-3 flex items-center gap-2 rounded-lg border px-3 py-2" style={{ borderColor: theme.border, background: theme.bg }}>
        <Ion name="business-outline" size={14} color={theme.textSecondary} />
        <input value={industry} onChange={(e) => setIndustry(e.target.value)} placeholder="Prospect industry (optional — e.g. HVAC, auto body)" className="flex-1 bg-transparent text-[13px] text-white placeholder:text-[#666] focus:outline-none" data-testid="coach-industry" />
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {topics.map((t) => (
          <button key={t.key} onClick={() => gen(t)} disabled={loading} data-testid={`coach-topic-${t.key}`} className="rounded-full px-3 py-1.5 text-xs font-bold"
            style={{ background: sel?.key === t.key ? theme.accent : theme.cardElevated, color: sel?.key === t.key ? "#000" : theme.text }}>{t.label}</button>
        ))}
      </div>
      {loading && <div className="mt-3 flex items-center gap-2 border-l-4 pl-3" style={{ borderColor: theme.border }}><Loader2 className="animate-spin" size={16} style={{ color: theme.accent }} /><p className="text-xs" style={{ color: theme.textSecondary }}>Coach is thinking about your {sel?.label.toLowerCase() || "script"}…</p></div>}
      {err && !loading && <p className="mt-3 border-l-4 border-[#E23E3E] pl-3 text-xs text-[#E23E3E]">{err}</p>}
      {resp && !loading && (
        <div className="mt-3 border-l-4 pl-3" style={{ borderColor: theme.accent }} data-testid="coach-response">
          <p className="text-[10px] font-black tracking-[1.5px]" style={{ color: theme.accent }}>{resp.label.toUpperCase()}</p>
          <p className="mt-1 whitespace-pre-line text-sm leading-5">{resp.script}</p>
          <div className="mt-2 flex flex-wrap gap-4">
            <button onClick={openSection} data-testid="coach-open-playbook" className="flex items-center gap-1 text-xs font-bold" style={{ color: theme.accent }}><Ion name="book-outline" size={14} color={theme.accent} />Read the matching playbook section</button>
            <button onClick={() => sel && gen(sel)} data-testid="coach-regenerate" className="flex items-center gap-1 text-xs font-bold" style={{ color: theme.textSecondary }}><Ion name="refresh" size={14} color={theme.textSecondary} />Regenerate</button>
          </div>
        </div>
      )}
    </div>
  );
};

const MetricRow = ({ m, count, target, unit, editable, onBump, theme }) => {
  const pct = target > 0 ? Math.min(100, (count / target) * 100) : 0;
  const met = target > 0 && count >= target;
  return (
    <div className={`${card} mt-2`} style={{ background: theme.card, borderColor: theme.border }} data-testid={`hc-metric-${m.key}`}>
      <div className="flex items-center">
        <span className="flex h-[38px] w-[38px] items-center justify-center rounded-xl" style={{ background: met ? theme.accent : theme.cardElevated }}><Ion name={m.icon} size={18} color={met ? "#000" : theme.text} /></span>
        <div className="ml-2 flex-1"><p className="text-sm font-extrabold">{m.label}</p><p className="mt-0.5 text-xs font-bold">{count} <span className="font-medium" style={{ color: theme.textSecondary }}>/ {target} {unit}</span></p></div>
        {editable && <div className="flex">
          <button onClick={() => onBump(-1)} disabled={count === 0} data-testid={`hc-dec-${m.key}`} className="ml-1.5 flex h-11 w-11 items-center justify-center rounded-xl disabled:opacity-35" style={{ background: theme.cardElevated }}><Ion name="remove" size={20} color={theme.text} /></button>
          <button onClick={() => onBump(1)} data-testid={`hc-inc-${m.key}`} className="ml-1.5 flex h-11 w-[52px] items-center justify-center rounded-xl" style={{ background: theme.accent }}><Ion name="add" size={20} color="#000" /></button>
        </div>}
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-[3px]" style={{ background: theme.cardElevated }}><div className="h-full rounded-[3px]" style={{ width: `${pct}%`, background: met ? theme.accent : "#F5A623" }} /></div>
    </div>
  );
};

const Header = ({ theme }) => {
  const back = useBack("/dashboard");
  return (
    <div className="flex items-center px-4 py-2">
      <button onClick={back} className="p-1.5" data-testid="back-btn"><Ion name="chevron-back" size={26} color={theme.text} /></button>
      <h1 className="ml-1 flex-1 text-lg font-extrabold" data-testid="page-title">Franchise Health Check</h1>
      <Ion name="fitness" size={22} color={theme.accent} />
    </div>
  );
};

export default function HealthCheck() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [targets, setTargets] = useState(null);
  const [today, setToday] = useState(null);
  const [week, setWeek] = useState(null);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("today");

  const load = useCallback(async () => {
    try {
      const [t, tod, wk] = await Promise.all([api("/health-check/targets"), api("/health-check/today"), api("/health-check/week")]);
      setTargets(t); setToday(tod); setWeek(wk); setNote(tod?.note || "");
    } catch (e) { window.alert(e.message || "Could not load Health Check."); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const bump = async (key, delta) => {
    setToday((p) => p && { ...p, counts: { ...p.counts, [key]: Math.max(0, (p.counts[key] || 0) + delta) } });
    try { setToday(await api("/health-check/increment", { method: "POST", body: { key, delta } })); api("/health-check/week").then(setWeek).catch(() => {}); }
    catch (e) { window.alert(e.message || "Could not update."); load(); }
  };
  const saveNote = async () => {
    setSaving(true);
    try { setToday(await api("/health-check/log", { method: "POST", body: { counts: today.counts, note } })); }
    catch (e) { window.alert(e.message || "Could not save note."); } finally { setSaving(false); }
  };
  const primary = useMemo(() => (targets?.metrics || []).filter((m) => !m.bonus), [targets]);
  const bonus = useMemo(() => (targets?.metrics || []).filter((m) => m.bonus), [targets]);
  if (loading) return <Screen testId="health-check-page"><Header theme={theme} /><Spinner /></Screen>;

  const obj = tab === "today" ? today : week;
  const score = obj ? Math.round(obj.score) : 0;
  const [sc, sl] = tone(score, theme.accent);
  const stage = today?.stage;
  const streak = today?.zero_activity_streak || 0;
  const row = (m) => <MetricRow key={m.key} m={m} theme={theme} unit={tab === "today" ? "today" : "this week"} editable={tab === "today"} onBump={(d) => bump(m.key, d)}
    count={tab === "today" ? today?.counts[m.key] || 0 : week?.totals[m.key] || 0} target={tab === "today" ? today?.targets[m.key] || 0 : week?.targets[m.key] || 0} />;
  const SecHead = ({ title, sub, top }) => <div className={`px-4 ${top ? "mt-6" : "mt-4"} mb-1`}><p className="text-[11px] font-extrabold tracking-[1.5px]" style={{ color: theme.textSecondary }}>{title}</p><p className="mt-1 text-xs leading-4" style={{ color: theme.textSecondary }}>{sub}</p></div>;

  return (
    <Screen testId="health-check-page">
      <Header theme={theme} />
      <div className="pb-12">
        <SalesTalkCoach theme={theme} />
        <div className={`${card} mt-2`} style={{ background: theme.card, borderColor: theme.border }} data-testid="health-score-card">
          <div className="flex items-center">
            <p className="text-[56px] font-black leading-[60px]" style={{ color: sc }} data-testid="health-score-value">{score}</p>
            <div className="ml-4 flex-1">
              <p className="text-xs font-black tracking-[2px]" style={{ color: sc }}>{sl}</p>
              <p className="mt-0.5 text-xs" style={{ color: theme.textSecondary }}>{tab === "today" ? "Today's score" : "This week's score"} · out of 100</p>
              {streak > 0 && <p className="mt-1.5 flex items-center gap-1 text-[11px] font-extrabold tracking-[0.5px] text-[#E23E3E]"><Ion name="warning" size={12} color="#E23E3E" />{streak} day{streak !== 1 ? "s" : ""} without activity</p>}
            </div>
          </div>
          {stage && <div className="mt-4 border-t pt-2" style={{ borderColor: theme.border }}>
            <p className="text-[10px] font-black tracking-[1.5px]" style={{ color: theme.accent }}>{stage.playbook === "first_120" ? "FIRST 120 DAYS" : "LEVEL UP"} · DAY {stage.day_number} · {stage.label.toUpperCase()}</p>
            <p className="mt-1 text-[13px] leading-[18px]">{stage.focus}</p>
          </div>}
        </div>
        <div className="mx-4 mb-2 mt-4 flex border-b" style={{ borderColor: theme.border }}>
          {[["today", "Today"], ["week", "This week"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`health-tab-${k}`} className="flex-1 border-b-2 py-2 text-[13px] font-bold tracking-[0.5px]" style={{ borderBottomColor: tab === k ? theme.accent : "transparent", color: tab === k ? theme.text : theme.textSecondary }}>{l}</button>
          ))}
        </div>
        {tab === "week" && week && (
          <div className="mb-2 flex justify-between px-4" data-testid="health-week-strip">
            {week.days.map((d) => {
              const total = Object.values(d.counts).reduce((a, b) => a + Number(b || 0), 0);
              return (
                <div key={d.date} className="flex flex-1 flex-col items-center">
                  <p className="mb-1 text-[10px] tracking-[0.5px]" style={{ color: theme.textSecondary }}>{d.weekday}</p>
                  <span className="flex h-9 w-9 items-center justify-center rounded-full border-2 text-xs font-extrabold"
                    style={{ background: d.is_future ? "#1A1A1A" : total > 0 ? theme.accent : "#2A2A2A", color: d.is_future ? "#666" : total > 0 ? "#000" : theme.textSecondary, borderColor: d.is_today ? theme.accent : "transparent" }}>{total > 0 ? total : ""}</span>
                </div>
              );
            })}
          </div>
        )}
        <SecHead title="DAILY COUNTERS" sub="Tap +1 as you go. Every knock, every call adds up. Playbook targets are per day." />
        {primary.map(row)}
        {bonus.length > 0 && <><SecHead top title="BONUS CREDIT" sub="Social posts and short videos aren't scored — but the playbook says consistency compounds." />{bonus.map(row)}</>}
        {tab === "today" && (
          <div className={`${card} mt-4`} style={{ background: theme.card, borderColor: theme.border }}>
            <p className="text-[11px] font-extrabold tracking-[1.5px]" style={{ color: theme.textSecondary }}>WIN OR BLOCKER OF THE DAY (optional)</p>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder="e.g. Landed a demo at Big Beam Manufacturing — quote due Friday." className="mt-2 min-h-[72px] w-full resize-none bg-transparent text-sm text-white placeholder:text-[#666] focus:outline-none" data-testid="hc-note" />
            <button disabled={saving} onClick={saveNote} data-testid="hc-save-note" className="mt-2 w-full rounded-lg py-3 text-xs font-black tracking-[1px] text-black disabled:opacity-60" style={{ background: theme.accent }}>{saving ? "Saving…" : "SAVE NOTE"}</button>
          </div>
        )}
        {stage?.principles?.length > 0 && (
          <button onClick={() => navigate(`/playbook/${stage.playbook === "first_120" ? "first-120" : "level-up"}/${stage.phase_key}`)} data-testid="health-check-principle-link"
            className="mx-4 mt-6 block w-[calc(100%-32px)] rounded-[14px] border-l-4 p-4 text-left" style={{ borderLeftColor: theme.accent, background: theme.card }}>
            <span className="mb-1.5 flex items-center justify-between"><span className="text-[11px] font-black tracking-[1.5px]" style={{ color: theme.accent }}>PLAYBOOK PRINCIPLE</span><span className="flex items-center text-[11px] font-black tracking-[0.6px]" style={{ color: theme.accent }}>Read this section<Ion name="chevron-forward" size={14} color={theme.accent} /></span></span>
            <span className="mt-1.5 block text-sm italic leading-5">“{stage.principles[Math.min(stage.day_number % stage.principles.length, stage.principles.length - 1)]}”</span>
            <span className="mt-2 block text-[11px] italic leading-[15px]" style={{ color: theme.textSecondary }}>From: {stage.label} — {stage.focus}</span>
          </button>
        )}
      </div>
    </Screen>
  );
}
